import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { User, Phone, Mail, Lock, Home, MapPin, Building, Calendar, Check, ArrowLeft, Hash, Eye, EyeOff } from 'lucide-react';
import { departmentsApi, designationsApi, teamsApi, employeesApi, orderCampaignApi } from '../services/api';
import { useTheme } from '../context/ThemeContext';

function AddEmployee() {
  const { currentTheme } = useTheme();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const isDark = currentTheme.textColor === '#FFFFFF';
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [teams, setTeams] = useState([]);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success'); // 'success' or 'error'
  const [showPassword, setShowPassword] = useState(false);
  const [orderCampaigns, setOrderCampaigns] = useState([]);
  const [loadingPincode, setLoadingPincode] = useState(false);

  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const [depts, desigs, tms, campaigns] = await Promise.all([
          departmentsApi.getAllDepartments(),
          designationsApi.getAllDesignations(),
          teamsApi.getAllTeams(),
          orderCampaignApi.getAllCampaigns()
        ]);
        console.log("orderCampaignApi", campaigns)
        setDepartments(depts);
        setDesignations(desigs);
        setTeams(tms);
        setOrderCampaigns(campaigns);
      } catch (error) {
        console.error('Error fetching dropdown data:', error);
      }
    };
    fetchDropdownData();
  }, []);

  useEffect(() => {
    if (id) {
      const fetchEmployeeData = async () => {
        try {
          setLoading(true);
          const emp = await employeesApi.getEmployeeById(id);
          setFormData({
            employeeId: emp.employeeId || '',
            name: emp.name || '',
            contactNumber: emp.contactNumber || emp.contact || '',
            email: emp.email || '',
            gender: (emp.gender || 'Male').charAt(0).toUpperCase() + (emp.gender || 'Male').slice(1).toLowerCase(),
            password: emp.password || '',
            locality: emp.addressStreet || emp.address || '',
            areaPinCode: emp.pincode || '',
            district: emp.district || '',
            state: emp.state || '',
            ipAddress: emp.ipAddress || '',
            orderCampaign: emp.orderCampaign || '',
            dailerCampaign: emp.dailerCampaign || '',
            dateOfJoining: emp.dateOfJoining || emp.createdAt ? new Date(emp.dateOfJoining || emp.createdAt).toISOString().split('T')[0] : '',
            department: emp.departmentId || '',
            designation: emp.designationId || '',
            team: emp.teamId || '',
            logPermission: 'Yes',
            maskNumber: 'Yes',
            downloadMaskedNumber: 'Yes'
          });
        } catch (error) {
          console.error("Error fetching employee:", error);
          showToast("Failed to fetch employee details", "error");
        } finally {
          setLoading(false);
        }
      };
      fetchEmployeeData();
    }
  }, [id]);

  // Form data state
  const [formData, setFormData] = useState({
    employeeId: '',
    name: '',
    contactNumber: '',
    email: '',
    gender: '',
    password: '',
    locality: '',
    areaPinCode: '',
    district: '',
    state: '',
    ipAddress: '',
    orderCampaign: '',
    dailerCampaign: '',
    dateOfJoining: '',
    department: '',
    designation: '',
    team: '',
    logPermission: 'Yes',
    maskNumber: 'Yes',
    downloadMaskedNumber: 'Yes'
  });

  const fetchDepartments = async () => {
    try {
      const data = await departmentsApi.getAllDepartments();
      setDepartments(data);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const fetchDesignations = async () => {
    try {
      const data = await designationsApi.getAllDesignations();
      setDesignations(data);
    } catch (error) {
      console.error('Error fetching designations:', error);
    }
  };

  const fetchTeams = async () => {
    try {
      const data = await teamsApi.getAllTeams();
      setTeams(data);
    } catch (error) {
      console.error('Error fetching teams:', error);
    }
  };

  useEffect(() => {
    // This duplicate effect can be removed or left as is, but we'll remove it to keep it clean.
  }, []);

  const showToast = (message, type = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setShowSuccessToast(true);
    setTimeout(() => setShowSuccessToast(false), 3000);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (touched[field]) {
      validateField(field, value);
    }
  };

  const handlePincodeChange = async (pincode) => {
    handleInputChange('areaPinCode', pincode);

    if (pincode.length === 6 && /^[0-9]{6}$/.test(pincode)) {
      setLoadingPincode(true);
      try {
        const response = await fetch(`https://api.postalpincode.in/pincode/${pincode}`);
        const data = await response.json();

        if (data && data.length > 0 && data[0].Status === 'Success') {
          const postOffices = data[0].PostOffice || [];
          if (postOffices.length > 0) {
            const firstPO = postOffices[0];
            const districtName = firstPO.District;
            const stateName = firstPO.State;

            setFormData(prev => ({
              ...prev,
              district: districtName,
              state: stateName,
            }));

            // Clear errors
            setErrors(prev => {
              const newErrors = { ...prev };
              delete newErrors.district;
              delete newErrors.state;
              return newErrors;
            });
          }
        }
      } catch (error) {
        console.error('Error fetching pincode data:', error);
      } finally {
        setLoadingPincode(false);
      }
    }
  };

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    validateField(field, formData[field]);
  };

  const validateField = (field, value) => {
    const newErrors = { ...errors };

    switch (field) {
      case 'name':
        if (!value.trim()) newErrors.name = 'Name is required';
        else delete newErrors.name;
        break;
      case 'contactNumber':
        if (!value || !value.trim()) newErrors.contactNumber = 'Contact number is required';
        else if (value.length < 10) newErrors.contactNumber = 'Number must be 10 digits';
        else if (!/^[6-9]\d{9}$/.test(value)) newErrors.contactNumber = 'Number must start with 6, 7, 8, or 9';
        else delete newErrors.contactNumber;
        break;
      case 'email':
        if (!value.trim()) newErrors.email = 'Email is required';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) newErrors.email = 'Invalid email';
        else delete newErrors.email;
        break;
      case 'gender':
        if (!value) newErrors.gender = 'Gender is required';
        else delete newErrors.gender;
        break;
      case 'password':
        if (!value || !value.trim()) {
          newErrors.password = 'Password is required';
        } else {
          const hasUppercase = /[A-Z]/.test(value);
          const hasNumber = /\d/.test(value);
          const hasSpecial = /[^A-Za-z0-9]/.test(value);
          const isLongEnough = value.length >= 6;

          if (!isLongEnough || !hasUppercase || !hasNumber || !hasSpecial) {
            const errorMsg = 'Password must be at least 6 characters, contain 1 uppercase letter, 1 number, and 1 special character';
            newErrors.password = errorMsg;
          } else {
            delete newErrors.password;
          }
        }
        break;
      case 'locality':
        if (!value.trim()) newErrors.locality = 'Locality is required';
        else delete newErrors.locality;
        break;
      case 'areaPinCode':
        if (!value.trim()) newErrors.areaPinCode = 'Area pin code is required';
        else if (!/^[1-9][0-9]{5}$/.test(value)) newErrors.areaPinCode = 'Please enter a valid 6-digit pin code';
        else delete newErrors.areaPinCode;
        break;
      case 'district':
        if (!value.trim()) newErrors.district = 'District is required';
        else delete newErrors.district;
        break;
      case 'state':
        if (!value.trim()) newErrors.state = 'State is required';
        else delete newErrors.state;
        break;
      case 'ipAddress':
        if (!value.trim()) newErrors.ipAddress = 'IP Address is required';
        else delete newErrors.ipAddress;
        break;
      case 'orderCampaign':
        if (!value) newErrors.orderCampaign = 'Order Campaign is required';
        else delete newErrors.orderCampaign;
        break;
      case 'department':
        if (!value) newErrors.department = 'Department is required';
        else delete newErrors.department;
        break;
      case 'designation':
        if (!value) newErrors.designation = 'Designation is required';
        else delete newErrors.designation;
        break;
      case 'team':
        if (!value) newErrors.team = 'Team is required';
        else delete newErrors.team;
        break;
      case 'dateOfJoining':
        if (!value) newErrors.dateOfJoining = 'Date of Joining is required';
        else delete newErrors.dateOfJoining;
        break;
      default:
        break;
    }

    setErrors(newErrors);
  };

  const validateForm = () => {
    const requiredFields = [
      'name', 'contactNumber', 'email', 'password', 'gender',
      'locality', 'areaPinCode', 'district', 'state',
      'ipAddress', 'orderCampaign',
      'dateOfJoining', 'department', 'designation', 'team'
    ];

    // Create new errors object to validate all fields at once
    const newErrors = { ...errors };
    const allTouched = { ...touched };

    requiredFields.forEach(field => {
      allTouched[field] = true;
      const value = formData[field];

      switch (field) {
        case 'name':
          if (!value || !value.trim()) newErrors.name = 'Name is required';
          else delete newErrors.name;
          break;
        case 'contactNumber':
          if (!value || !value.trim()) newErrors.contactNumber = 'Contact number is required';
          else if (value.length < 10) newErrors.contactNumber = 'Number must be 10 digits';
          else if (!/^[6-9]\d{9}$/.test(value)) newErrors.contactNumber = 'Number must start with 6, 7, 8, or 9';
          else delete newErrors.contactNumber;
          break;
        case 'email':
          if (!value || !value.trim()) newErrors.email = 'Email is required';
          else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) newErrors.email = 'Invalid email';
          else delete newErrors.email;
          break;
        case 'gender':
          if (!value) newErrors.gender = 'Gender is required';
          else delete newErrors.gender;
          break;
        case 'password':
          if (!value || !value.trim()) {
            newErrors.password = 'Password is required';
          } else {
            const hasUppercase = /[A-Z]/.test(value);
            const hasNumber = /\d/.test(value);
            const hasSpecial = /[^A-Za-z0-9]/.test(value);
            const isLongEnough = value.length >= 6;

            if (!isLongEnough || !hasUppercase || !hasNumber || !hasSpecial) {
              const errorMsg = 'Password must be at least 6 characters, contain 1 uppercase letter, 1 number, and 1 special character';
              newErrors.password = errorMsg;
            } else {
              delete newErrors.password;
            }
          }
          break;
        case 'locality':
          if (!value || !value.trim()) newErrors.locality = 'Locality is required';
          else delete newErrors.locality;
          break;
        case 'areaPinCode':
          if (!value || !value.trim()) newErrors.areaPinCode = 'Area pin code is required';
          else if (!/^[1-9][0-9]{5}$/.test(value)) newErrors.areaPinCode = 'Please enter a valid 6-digit pin code';
          else delete newErrors.areaPinCode;
          break;
        case 'district':
          if (!value || !value.trim()) newErrors.district = 'District is required';
          else delete newErrors.district;
          break;
        case 'state':
          if (!value || !value.trim()) newErrors.state = 'State is required';
          else delete newErrors.state;
          break;
        case 'ipAddress':
          if (!value || !value.trim()) newErrors.ipAddress = 'IP Address is required';
          else delete newErrors.ipAddress;
          break;
        case 'orderCampaign':
          if (!value) newErrors.orderCampaign = 'Order Campaign is required';
          else delete newErrors.orderCampaign;
          break;
        case 'department':
          if (!value) newErrors.department = 'Department is required';
          else delete newErrors.department;
          break;
        case 'designation':
          if (!value) newErrors.designation = 'Designation is required';
          else delete newErrors.designation;
          break;
        case 'team':
          if (!value) newErrors.team = 'Team is required';
          else delete newErrors.team;
          break;
        case 'dateOfJoining':
          if (!value) newErrors.dateOfJoining = 'Date of Joining is required';
          else delete newErrors.dateOfJoining;
          break;
        default:
          break;
      }
    });

    setErrors(newErrors);
    setTouched(allTouched);

    return requiredFields.every(field => {
      if (newErrors[field]) return false;
      const value = formData[field];
      return value && (typeof value === 'string' ? value.trim() !== '' : true);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Mark all fields as touched
    const allTouched = {};
    Object.keys(formData).forEach(key => {
      allTouched[key] = true;
    });
    setTouched(allTouched);

    // Validate form
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      const employeePayload = {
        name: formData.name,
        email: formData.email,
        contactNumber: formData.contactNumber,
        gender: formData.gender.toUpperCase(), // Convert 'Male'/'Female' to 'MALE'/'FEMALE'
        addressStreet: formData.locality,
        pincode: formData.areaPinCode,
        district: formData.district,
        state: formData.state,
        departmentId: parseInt(formData.department),
        designationId: parseInt(formData.designation),
        teamId: parseInt(formData.team),
        password: formData.password,
        ipAddress: formData.ipAddress,
        orderCampaign: formData.orderCampaign,
        dailerCampaign: formData.dailerCampaign,
        dateOfJoining: formData.dateOfJoining
      };

      // Only include employeeId if it has a value (for new employees, it will be empty)
      if (formData.employeeId && formData.employeeId.trim()) {
        employeePayload.employeeId = formData.employeeId;
      }

      if (isEditMode) {
        await employeesApi.updateEmployee(id, employeePayload);
        showToast('Employee updated successfully!', 'success');
      } else {
        await employeesApi.createEmployee(employeePayload);
        showToast('Employee added successfully!', 'success');
      }

      setTimeout(() => {
        navigate('/employees?tab=employees');
      }, 1500);
    } catch (error) {
      console.error('Error adding employee:', error);
      const errorMessage = error?.error || error?.message || 'Failed to add employee';
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 font-sans ${isDark ? 'bg-[#0f172a] text-white' : 'bg-[#F0FDF4] text-gray-900'}`}>
      <div className="max-w-[1400px] mx-auto p-4 md:p-8">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/employees")}
              className="w-12 h-12 rounded-full bg-white border border-gray-200 flex items-center justify-center hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm group"
            >
              <ArrowLeft className="w-5 h-5 text-gray-500 group-hover:text-gray-700" />
            </button>
            <div>
              <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>{isEditMode ? 'Edit Employee' : 'New Employee'}</h1>
              <p className={`${isDark ? 'text-gray-400' : 'text-gray-500'} mt-1 text-sm`}>{isEditMode ? 'Update existing employment profile' : 'Create a new employment profile'}</p>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="grid grid-cols-12 gap-8">

          {/* LEFT COLUMN - Primary Info */}
          <div className="col-span-12 lg:col-span-8 space-y-8">

            {/* Basic Details Card */}
            <div className={`${isDark ? 'bg-[#1e293b] border-white/10' : 'bg-white border-gray-100'} rounded-2xl shadow-sm border p-8`}>
              <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-100">
                <div className="w-10 h-10 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: `${currentTheme.color}20` }}>
                  <User className="w-5 h-5" style={{ color: currentTheme.color }} />
                </div>
                <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>Basic Details</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Employee ID */}
                <div className="col-span-2 md:col-span-1">
                  <label className={`block text-sm font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>Employee ID</label>
                  <div className="relative group">
                    <Hash className="absolute left-3.5 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                    <input
                      type="text"
                      value={formData.employeeId}
                      onChange={(e) => handleInputChange('employeeId', e.target.value)}
                      placeholder="e.g. EMP-001 (Optional)"
                      className={`w-full pl-11 pr-4 py-3 ${isDark ? 'bg-[#0f172a] text-white border-white/10' : 'bg-gray-50 text-gray-900 border-gray-200'} rounded-xl focus:bg-white focus:ring-2 transition-all outline-none`}
                      style={{
                        borderColor: formData.employeeId ? currentTheme.color : '#E5E7EB',
                      }}
                    />
                  </div>
                </div>

                {/* Name */}
                <div className="col-span-2 md:col-span-1">
                  <label className={`block text-sm font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>Name <span className="text-red-500">*</span></label>
                  <div className="relative group">
                    <User className="absolute left-3.5 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value.replace(/[^a-zA-Z\s]/g, ''))}
                      onBlur={() => handleBlur('name')}
                      placeholder="Full Name"
                      className={`w-full pl-11 pr-4 py-3 ${isDark ? 'bg-[#0f172a] text-white border-white/10' : 'bg-gray-50 text-gray-900 border-gray-200'} rounded-xl focus:bg-white focus:ring-2 transition-all outline-none`}
                      style={{
                        borderColor: errors.name && touched.name ? '#EF4444' : (formData.name ? currentTheme.color : '#E5E7EB'),
                        boxShadow: errors.name && touched.name ? '0 0 0 2px #FEE2E2' : undefined
                      }}
                    />
                  </div>
                  {errors.name && touched.name && <p className="text-red-500 text-xs mt-1 ml-1">{errors.name}</p>}
                </div>

                {/* Contact */}
                <div className="col-span-2 md:col-span-1">
                  <label className={`block text-sm font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>Contact Number <span className="text-red-500">*</span></label>
                  <div className="relative group">
                    <Phone className="absolute left-3.5 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                    <input
                      type="text"
                      value={formData.contactNumber}
                      onChange={(e) => handleInputChange('contactNumber', e.target.value.replace(/\D/g, '').slice(0, 10))}
                      onBlur={() => handleBlur('contactNumber')}
                      placeholder="10-digit mobile number"
                      className={`w-full pl-11 pr-4 py-3 ${isDark ? 'bg-[#0f172a] text-white border-white/10' : 'bg-gray-50 text-gray-900 border-gray-200'} rounded-xl focus:bg-white focus:ring-2 transition-all outline-none`}
                      style={{
                        borderColor: errors.contactNumber && touched.contactNumber ? '#EF4444' : (formData.contactNumber ? currentTheme.color : '#E5E7EB'),
                        boxShadow: errors.contactNumber && touched.contactNumber ? '0 0 0 2px #FEE2E2' : undefined
                      }}
                    />
                  </div>
                  {errors.contactNumber && touched.contactNumber && <p className="text-red-500 text-xs mt-1 ml-1">{errors.contactNumber}</p>}
                </div>

                {/* Email */}
                <div className="col-span-2 md:col-span-1">
                  <label className={`block text-sm font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>Email Address <span className="text-red-500">*</span></label>
                  <div className="relative group">
                    <Mail className="absolute left-3.5 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      onBlur={() => handleBlur('email')}
                      autoComplete="off"
                      placeholder="employee@company.com"
                      className={`w-full pl-11 pr-4 py-3 ${isDark ? 'bg-[#0f172a] text-white border-white/10' : 'bg-gray-50 text-gray-900 border-gray-200'} rounded-xl focus:bg-white focus:ring-2 transition-all outline-none`}
                      style={{
                        borderColor: errors.email && touched.email ? '#EF4444' : (formData.email ? currentTheme.color : '#E5E7EB'),
                        boxShadow: errors.email && touched.email ? '0 0 0 2px #FEE2E2' : undefined
                      }}
                    />
                  </div>
                  {errors.email && touched.email && <p className="text-red-500 text-xs mt-1 ml-1">{errors.email}</p>}
                </div>

                {/* Gender */}
                <div className="col-span-2 md:col-span-1">
                  <label className={`block text-sm font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>Gender <span className="text-red-500">*</span></label>
                  <div
                    className={`flex gap-2 p-1.5 ${isDark ? 'bg-[#0f172a] border-white/10' : 'bg-gray-50 border-gray-200'} rounded-xl border ${errors.gender && touched.gender ? 'border-red-500' : ''}`}
                    style={{
                      boxShadow: errors.gender && touched.gender ? '0 0 0 2px #FEE2E2' : undefined
                    }}
                  >
                    {['Male', 'Female'].map((gender) => (
                      <button
                        key={gender}
                        type="button"
                        onClick={() => handleInputChange('gender', gender)}
                        onBlur={() => handleBlur('gender')}
                        className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${formData.gender === gender
                          ? 'shadow-sm ring-1 ring-gray-100'
                          : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                          }`}
                        style={{
                          backgroundColor: formData.gender === gender ? currentTheme.color : 'transparent',
                          color: formData.gender === gender ? 'white' : '#6B7280'
                        }}
                      >
                        {gender}
                      </button>
                    ))}
                  </div>
                  {errors.gender && touched.gender && <p className="text-red-500 text-xs mt-1 ml-1">{errors.gender}</p>}
                </div>

                {/* Password */}
                <div className="col-span-2 md:col-span-1">
                  <label className={`block text-sm font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>Password <span className="text-red-500">*</span></label>
                  <div className="relative group">
                    <Lock className="absolute left-3.5 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                    <input
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      onBlur={() => handleBlur('password')}
                      autoComplete="new-password"
                      placeholder="Set login password"
                      className={`w-full pl-11 pr-12 py-3 ${isDark ? 'bg-[#0f172a] text-white border-white/10' : 'bg-gray-50 text-gray-900 border-gray-200'} rounded-xl focus:bg-white focus:ring-2 transition-all outline-none`}
                      style={{
                        borderColor: errors.password && touched.password ? '#EF4444' : (formData.password ? currentTheme.color : '#E5E7EB'),
                        boxShadow: errors.password && touched.password ? '0 0 0 2px #FEE2E2' : undefined
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3.5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  {errors.password && touched.password && <p className="text-red-500 text-xs mt-1 ml-1">{errors.password}</p>}
                </div>
              </div>
            </div>

            {/* Address Details Card */}
            <div className={`${isDark ? 'bg-[#1e293b] border-white/10' : 'bg-white border-gray-100'} rounded-2xl shadow-sm border p-8`}>
              <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-100">
                <div className="w-10 h-10 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: `${currentTheme.color}20` }}>
                  <MapPin className="w-5 h-5" style={{ color: currentTheme.color }} />
                </div>
                <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>Address Details</h3>
              </div>

              <div className="space-y-6">
                <div>
                  <label className={`block text-sm font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>Locality / Street Address <span className="text-red-500">*</span></label>
                  <div className="relative group">
                    <Home className="absolute left-3.5 top-3.5 w-5 h-5 text-gray-400 group-focus-within:text-orange-500 transition-colors" />
                    <textarea
                      value={formData.locality}
                      onChange={(e) => handleInputChange('locality', e.target.value)}
                      onBlur={() => handleBlur('locality')}
                      placeholder="House No., Building, Street Area"
                      rows={2}
                      className={`w-full pl-11 pr-4 py-3 ${isDark ? 'bg-[#0f172a] text-white border-white/10' : 'bg-gray-50 text-gray-900 border-gray-200'} rounded-xl focus:bg-white focus:ring-2 transition-all outline-none resize-none ${errors.locality && touched.locality ? 'border-red-500 focus:border-red-500' : ''
                        }`}
                      style={{ borderColor: !errors.locality && formData.locality ? currentTheme.color : '#E5E7EB' }}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Pincode */}
                  <div className="relative">
                    <label className={`block text-sm font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>Pincode <span className="text-red-500">*</span></label>
                    <div className="relative">
                      <input
                        type="text"
                        value={formData.areaPinCode}
                        onChange={(e) => handlePincodeChange(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        onBlur={() => handleBlur('areaPinCode')}
                        placeholder="000000"
                        className={`w-full px-4 py-3 ${isDark ? 'bg-[#0f172a] text-white border-white/10' : 'bg-gray-50 text-gray-900 border-gray-200'} rounded-xl focus:bg-white focus:ring-2 transition-all outline-none ${errors.areaPinCode && touched.areaPinCode ? 'border-red-500 focus:border-red-500' : ''
                          }`}
                        style={{ borderColor: !errors.areaPinCode && formData.areaPinCode ? currentTheme.color : '#E5E7EB', boxShadow: !errors.areaPinCode && formData.areaPinCode ? `0 0 0 2px ${currentTheme.color}20` : undefined }}
                      />
                      {loadingPincode && (
                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                          <span className="animate-spin h-5 w-5 border-2 border-teal-500 border-t-transparent rounded-full block"></span>
                        </div>
                      )}
                    </div>
                    {errors.areaPinCode && touched.areaPinCode && <p className="text-red-500 text-xs mt-1 ml-1">{errors.areaPinCode}</p>}
                  </div>

                  {/* State */}
                  <div>
                    <label className={`block text-sm font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>State <span className="text-red-500">*</span></label>
                    <div className="relative group">
                      <MapPin className="absolute left-3.5 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                      <input
                        type="text"
                        value={formData.state}
                        onChange={(e) => handleInputChange('state', e.target.value)}
                        onBlur={() => handleBlur('state')}
                        placeholder="Enter State"
                        className={`w-full pl-11 pr-4 py-3 ${isDark ? 'bg-[#0f172a] text-white border-white/10' : 'bg-gray-50 text-gray-900 border-gray-200'} rounded-xl focus:bg-white focus:ring-2 transition-all outline-none ${errors.state && touched.state ? 'border-red-500 focus:border-red-500' : ''
                          }`}
                        style={{
                          borderColor: errors.state && touched.state ? '#EF4444' : (formData.state ? currentTheme.color : '#E5E7EB'),
                          boxShadow: errors.state && touched.state ? '0 0 0 2px #FEE2E2' : undefined
                        }}
                      />
                    </div>
                    {errors.state && touched.state && <p className="text-red-500 text-xs mt-1 ml-1">{errors.state}</p>}
                  </div>

                  {/* District */}
                  <div>
                    <label className={`block text-sm font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>District <span className="text-red-500">*</span></label>
                    <div className="relative group">
                      <MapPin className="absolute left-3.5 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                      <input
                        type="text"
                        value={formData.district}
                        onChange={(e) => handleInputChange('district', e.target.value)}
                        onBlur={() => handleBlur('district')}
                        placeholder="Enter District"
                        className={`w-full pl-11 pr-4 py-3 ${isDark ? 'bg-[#0f172a] text-white border-white/10' : 'bg-gray-50 text-gray-900 border-gray-200'} rounded-xl focus:bg-white focus:ring-2 transition-all outline-none ${errors.district && touched.district ? 'border-red-500 focus:border-red-500' : ''
                          }`}
                        style={{
                          borderColor: errors.district && touched.district ? '#EF4444' : (formData.district ? currentTheme.color : '#E5E7EB'),
                          boxShadow: errors.district && touched.district ? '0 0 0 2px #FEE2E2' : undefined
                        }}
                      />
                    </div>
                    {errors.district && touched.district && <p className="text-red-500 text-xs mt-1 ml-1">{errors.district}</p>}
                  </div>
                </div>
              </div>
            </div>

            {/* Technical Details Card */}
            <div className={`${isDark ? 'bg-[#1e293b] border-white/10' : 'bg-white border-gray-100'} rounded-2xl shadow-sm border p-8`}>
              <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-100">
                <div className="w-10 h-10 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: `${currentTheme.color}20` }}>
                  <Lock className="w-5 h-5" style={{ color: currentTheme.color }} />
                </div>
                <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>System & Access</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div>
                  <label className={`block text-sm font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>IP Address Restriction <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={formData.ipAddress}
                    onChange={(e) => handleInputChange('ipAddress', e.target.value)}
                    onBlur={() => handleBlur('ipAddress')}
                    placeholder="e.g. 192.168.1.1"
                    className={`w-full px-4 py-3 ${isDark ? 'bg-[#0f172a] text-white border-white/10' : 'bg-gray-50 text-gray-900 border-gray-200'} rounded-xl focus:bg-white focus:ring-2 transition-all outline-none`}
                    style={{
                      borderColor: errors.ipAddress && touched.ipAddress ? '#EF4444' : (formData.ipAddress ? currentTheme.color : '#E5E7EB'),
                      boxShadow: errors.ipAddress && touched.ipAddress ? '0 0 0 2px #FEE2E2' : undefined
                    }}
                  />
                  {errors.ipAddress && touched.ipAddress && <p className="text-red-500 text-xs mt-1 ml-1">{errors.ipAddress}</p>}
                </div>
                <div>
                  <label className={`block text-sm font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>Order Campaign <span className="text-red-500">*</span></label>
                  <select
                    value={formData.orderCampaign}
                    onChange={(e) => handleInputChange('orderCampaign', e.target.value)}
                    onBlur={() => handleBlur('orderCampaign')}
                    className={`w-full px-4 py-3 ${isDark ? 'bg-[#0f172a] text-white border-white/10' : 'bg-gray-50 text-gray-900 border-gray-200'} rounded-xl focus:bg-white focus:ring-2 transition-all outline-none`}
                    style={{
                      borderColor: errors.orderCampaign && touched.orderCampaign ? '#EF4444' : (formData.orderCampaign ? currentTheme.color : '#E5E7EB'),
                      boxShadow: errors.orderCampaign && touched.orderCampaign ? '0 0 0 2px #FEE2E2' : undefined
                    }}
                  >
                    <option value="">Select Campaign</option>
                    {orderCampaigns.map(campaign => (
                      <option key={campaign.id} value={campaign.id}>{campaign.campaignName}</option>
                    ))}
                  </select>
                  {errors.orderCampaign && touched.orderCampaign && <p className="text-red-500 text-xs mt-1 ml-1">{errors.orderCampaign}</p>}
                </div>
                <div>
                  <label className={`block text-sm font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>Dialer Campaign</label>
                  <input
                    type="text"
                    value={formData.dailerCampaign}
                    onChange={(e) => handleInputChange('dailerCampaign', e.target.value)}
                    placeholder="Campaign Name"
                    className={`w-full px-4 py-3 ${isDark ? 'bg-[#0f172a] text-white border-white/10' : 'bg-gray-50 text-gray-900 border-gray-200'} rounded-xl focus:bg-white focus:ring-2 transition-all outline-none`}
                    style={{ borderColor: formData.dailerCampaign ? currentTheme.color : '#E5E7EB' }}
                  />
                </div>
              </div>
            </div>

          </div>

          {/* RIGHT COLUMN - Employment & Permissions */}
          <div className="col-span-12 lg:col-span-4 space-y-8">
            <div className={`${isDark ? 'bg-[#1e293b] border-white/10' : 'bg-white border-gray-100'} rounded-2xl shadow-sm border p-8 h-full`}>
              <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-100">
                <div className="w-10 h-10 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: `${currentTheme.color}20` }}>
                  <Building className="w-5 h-5" style={{ color: currentTheme.color }} />
                </div>
                <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>Employment</h3>
              </div>

              <div className="space-y-6">
                <div>
                  <label className={`block text-sm font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>Date of Joining <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <input
                      type="date"
                      value={formData.dateOfJoining}
                      onChange={(e) => handleInputChange('dateOfJoining', e.target.value)}
                      onBlur={() => handleBlur('dateOfJoining')}
                      className={`w-full px-4 py-3 ${isDark ? 'bg-[#0f172a] text-white border-white/10' : 'bg-white border-gray-200'} rounded-xl focus:ring-2 transition-all outline-none`}
                      style={{
                        borderColor: errors.dateOfJoining && touched.dateOfJoining ? '#EF4444' : (formData.dateOfJoining ? currentTheme.color : '#E5E7EB'),
                        boxShadow: errors.dateOfJoining && touched.dateOfJoining ? '0 0 0 2px #FEE2E2' : undefined
                      }}
                    />
                  </div>
                  {errors.dateOfJoining && touched.dateOfJoining && <p className="text-red-500 text-xs mt-1 ml-1">{errors.dateOfJoining}</p>}
                </div>

                <div>
                  <label className={`block text-sm font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>Department <span className="text-red-500">*</span></label>
                  <select
                    value={formData.department}
                    onChange={(e) => handleInputChange('department', e.target.value)}
                    onBlur={() => handleBlur('department')}
                    className={`w-full px-4 py-3 ${isDark ? 'bg-[#0f172a] text-white border-white/10' : 'bg-white border-gray-200'} rounded-xl focus:ring-2 transition-all outline-none`}
                    style={{
                      borderColor: errors.department && touched.department ? '#EF4444' : (formData.department ? currentTheme.color : '#E5E7EB'),
                      boxShadow: errors.department && touched.department ? '0 0 0 2px #FEE2E2' : undefined
                    }}
                  >
                    <option value="">Select Department</option>
                    {departments.map(dept => (
                      <option key={dept.id} value={dept.id}>{dept.name}</option>
                    ))}
                  </select>
                  {errors.department && touched.department && <p className="text-red-500 text-xs mt-1 ml-1">{errors.department}</p>}
                </div>

                <div>
                  <label className={`block text-sm font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>Designation <span className="text-red-500">*</span></label>
                  <select
                    value={formData.designation}
                    onChange={(e) => handleInputChange('designation', e.target.value)}
                    onBlur={() => handleBlur('designation')}
                    className={`w-full px-4 py-3 ${isDark ? 'bg-[#0f172a] text-white border-white/10' : 'bg-white border-gray-200'} rounded-xl focus:ring-2 transition-all outline-none`}
                    style={{
                      borderColor: errors.designation && touched.designation ? '#EF4444' : (formData.designation ? currentTheme.color : '#E5E7EB'),
                      boxShadow: errors.designation && touched.designation ? '0 0 0 2px #FEE2E2' : undefined
                    }}
                  >
                    <option value="">Select Designation</option>
                    {designations.map(desig => (
                      <option key={desig.id} value={desig.id}>{desig.name}</option>
                    ))}
                  </select>
                  {errors.designation && touched.designation && <p className="text-red-500 text-xs mt-1 ml-1">{errors.designation}</p>}
                </div>

                <div>
                  <label className={`block text-sm font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>Team <span className="text-red-500">*</span></label>
                  <select
                    value={formData.team}
                    onChange={(e) => handleInputChange('team', e.target.value)}
                    onBlur={() => handleBlur('team')}
                    className={`w-full px-4 py-3 ${isDark ? 'bg-[#0f172a] text-white border-white/10' : 'bg-white border-gray-200'} rounded-xl focus:ring-2 transition-all outline-none`}
                    style={{
                      borderColor: errors.team && touched.team ? '#EF4444' : (formData.team ? currentTheme.color : '#E5E7EB'),
                      boxShadow: errors.team && touched.team ? '0 0 0 2px #FEE2E2' : undefined
                    }}
                  >
                    <option value="">Select Team</option>
                    {teams.map(team => (
                      <option key={team.id} value={team.id}>{team.name}</option>
                    ))}
                  </select>
                  {errors.team && touched.team && <p className="text-red-500 text-xs mt-1 ml-1">{errors.team}</p>}
                </div>


                {/* Permissions Section */}
                <div className={`pt-6 border-t ${isDark ? 'border-white/10' : 'border-gray-100'}`}>
                  <h4 className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'} uppercase tracking-wider mb-4`}>Permissions</h4>

                  <div className="space-y-4">
                    {/* Log Permission */}
                    <div className={`flex items-center justify-between p-3 ${isDark ? 'bg-[#0f172a]' : 'bg-gray-50'} rounded-xl`}>
                      <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Log Permission</span>
                      <div className={`flex ${isDark ? 'bg-[#1e293b] border-white/10' : 'bg-white border-gray-200'} rounded-lg p-1 border`}>
                        <button
                          type="button"
                          onClick={() => handleInputChange('logPermission', 'Yes')}
                          className={`px-3 py-1 text-xs font-semibold rounded-md transition-all`}
                          style={{
                            backgroundColor: formData.logPermission === 'Yes' ? currentTheme.color : 'transparent',
                            color: formData.logPermission === 'Yes' ? 'white' : '#6B7280'
                          }}
                        >
                          Yes
                        </button>
                        <button
                          type="button"
                          onClick={() => handleInputChange('logPermission', 'No')}
                          className={`px-3 py-1 text-xs font-semibold rounded-md transition-all ${formData.logPermission === 'No' ? 'bg-red-500 text-white shadow-sm' : 'text-gray-500'}`}
                        >
                          No
                        </button>
                      </div>
                    </div>

                    {/* Mask Number */}
                    <div className={`flex items-center justify-between p-3 ${isDark ? 'bg-[#0f172a]' : 'bg-gray-50'} rounded-xl`}>
                      <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Mask Number</span>
                      <div className={`flex ${isDark ? 'bg-[#1e293b] border-white/10' : 'bg-white border-gray-200'} rounded-lg p-1 border`}>
                        <button
                          type="button"
                          onClick={() => handleInputChange('maskNumber', 'Yes')}
                          className={`px-3 py-1 text-xs font-semibold rounded-md transition-all`}
                          style={{
                            backgroundColor: formData.maskNumber === 'Yes' ? currentTheme.color : 'transparent',
                            color: formData.maskNumber === 'Yes' ? 'white' : '#6B7280'
                          }}
                        >
                          Yes
                        </button>
                        <button
                          type="button"
                          onClick={() => handleInputChange('maskNumber', 'No')}
                          className={`px-3 py-1 text-xs font-semibold rounded-md transition-all ${formData.maskNumber === 'No' ? 'bg-red-500 text-white shadow-sm' : 'text-gray-500'}`}
                        >
                          No
                        </button>
                      </div>
                    </div>

                    {/* Download Masked Number */}
                    <div className={`flex items-center justify-between p-3 ${isDark ? 'bg-[#0f172a]' : 'bg-gray-50'} rounded-xl`}>
                      <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Download Masked</span>
                      <div className={`flex ${isDark ? 'bg-[#1e293b] border-white/10' : 'bg-white border-gray-200'} rounded-lg p-1 border`}>
                        <button
                          type="button"
                          onClick={() => handleInputChange('downloadMaskedNumber', 'Yes')}
                          className={`px-3 py-1 text-xs font-semibold rounded-md transition-all`}
                          style={{
                            backgroundColor: formData.downloadMaskedNumber === 'Yes' ? currentTheme.color : 'transparent',
                            color: formData.downloadMaskedNumber === 'Yes' ? 'white' : '#6B7280'
                          }}
                        >
                          Yes
                        </button>
                        <button
                          type="button"
                          onClick={() => handleInputChange('downloadMaskedNumber', 'No')}
                          className={`px-3 py-1 text-xs font-semibold rounded-md transition-all ${formData.downloadMaskedNumber === 'No' ? 'bg-red-500 text-white shadow-sm' : 'text-gray-500'}`}
                        >
                          No
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Fixed Bottom Bar / Submit Action */}
          <div className="col-span-12 mt-4 flex items-center justify-end gap-4 pb-12">
            <button
              type="button"
              onClick={() => navigate('/employees')}
              className="px-8 py-3 bg-white border border-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-8 py-3 text-white font-semibold rounded-xl shadow-lg transition-all flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
              style={{ backgroundColor: currentTheme.color }}
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>{isEditMode ? 'Updating Profile...' : 'Creating Profile...'}</span>
                </>
              ) : (
                <>
                  <Check className="w-5 h-5" />
                  <span>{isEditMode ? 'Update Employee' : 'Create Employee'}</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {showSuccessToast && (
        <div className={`fixed top-4 right-4 z-50 px-6 py-4 rounded-xl shadow-2xl flex items-center space-x-3 transform transition-all duration-500 ${isDark ? 'bg-[#1e293b] border-white/10' : 'bg-white border-l-4'}`}
          style={{ borderLeftColor: toastType === 'success' ? currentTheme.color : '#EF4444' }}>
          <div className={`p-2 rounded-full`} style={{ backgroundColor: toastType === 'success' ? `${currentTheme.color}20` : '#FEE2E2', color: toastType === 'success' ? currentTheme.color : '#EF4444' }}>
            {toastType === 'success' ? <Check className="w-5 h-5" /> : <div className="w-5 h-5 font-bold text-center">!</div>}
          </div>
          <div>
            <h4 className={`font-bold ${toastType === 'success' ? (isDark ? 'text-white' : 'text-gray-800') : 'text-red-600'}`}>
              {toastType === 'success' ? 'Success' : 'Error'}
            </h4>
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{toastMessage}</p>
          </div>
        </div>
      )
      }
    </div >
  );
}

export default AddEmployee;
